import { FC, ReactNode } from 'react';
import { css, Theme, useTheme } from '@emotion/react';
import { transparentize } from 'polished';
import { Breadcrumbs, BreadcrumbsItemProps } from 'client/components/Breadcrumbs';
import type { AvatarProps } from 'client/components/Other/Avatar';
import { ListItemFigure, ListItemFigureProps } from 'client/components/Other/ListItemFigure';
import { getFeaturesFromState, useStatusColorToHex } from 'client/components/Status/statusUtils';
import { Tabs, TabsProps } from 'client/components/Tabs/Tabs';
import { Typography } from 'client/components/Typography';
import { borderRadius, layoutStyles } from 'client/Theme';

export interface SubheaderProps {
  title: string | JSX.Element;
  subtitle?: string | JSX.Element;
  icon?: JSX.Element;
  image?: AvatarProps | null;
  state?: ListItemFigureProps['state'];
  statusBar?: boolean;
  alertBar?: JSX.Element;
  type?: string | null;
  breadcrumbs?: BreadcrumbsItemProps[];
  actions?: JSX.Element;
  tabs?: TabsProps | null;
  children?: ReactNode;
}

export const Subheader: FC<SubheaderProps> = ({
  title,
  subtitle,
  icon,
  image = null,
  state: stateProp = null,
  type = null,
  breadcrumbs = null,
  actions,
  statusBar = false,
  alertBar = null,
  tabs = null,
  children,
  ...other
}) => {
  const size = type ? 40 : 32;
  const theme = useTheme();
  const stateFeatures = getFeaturesFromState(stateProp);
  const stateColors = useStatusColorToHex();

  let color = theme.color.primary[100];
  if (stateProp) color = stateColors[stateFeatures.color];

  return (
    <div css={subheaderStyle} {...other}>
      {breadcrumbs && !!breadcrumbs.length && (
        <nav css={breadcrumbsStyle}>
          <Breadcrumbs items={breadcrumbs} />
        </nav>
      )}

      <header>
        <section>
          {(icon || image) && <ListItemFigure icon={icon} image={image} size={size} state={stateProp} className="icon" css={figureStyle} />}

          <div css={contentStyle}>
            <div css={[titleStyle, !!type && titleTypeStyle, statusBar && titleStatusBarStyle]}>
              {statusBar && <div css={[statusBarStyle, !!type && statusBarTypeStyle]} style={{ backgroundColor: color }} />}
              {!!type && (
                <Typography variant="caption" css={typeStyle}>
                  {type}
                </Typography>
              )}
              <Typography variant="h4">{title}</Typography>
            </div>
            {subtitle && (
              <Typography variant="body1" component="p" css={[subtitleStyle, statusBar && subtitleStatusBarStyle]}>
                {subtitle}
              </Typography>
            )}
          </div>
        </section>

        {actions && <div css={actionsStyle}>{actions}</div>}

        {children}
      </header>

      {alertBar}

      {!!tabs && (
        <nav css={navigationStyle}>
          <Tabs {...tabs} />
        </nav>
      )}
    </div>
  );
};

const subheaderStyle = (theme: Theme) => css`
  position: relative;
  padding: 0 0 2rem 0;

  &:after {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    width: 100%;
    background-color: ${theme.color.grey[60]};
  }

  header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    ${layoutStyles}
    padding: 2rem 16px 0 16px;

    ${theme.mediaQuery.medium(css`
      flex-direction: row;
      align-items: center;
    `)}

    ${theme.mediaQuery.large(css`
      padding: 2rem 0 0 0;
    `)}

    > section {
      display: flex;
      align-items: center;
    }
  }
`;

const figureStyle = css`
  align-self: flex-start;
  margin-right: 8px;
`;

const contentStyle = css`
  flex: 1;
`;

const titleStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 32px;

  > .caption {
    margin-top: -2px;
  }

  > .typography.h4 {
    // margin-top: 4px;
    line-height: 1;
  }
`;

const titleStatusBarStyle = css`
  padding-left: 14px;

  &:not(:only-child) {
    margin-bottom: 4px;
  }
`;

const titleTypeStyle = css`
  min-height: 40px;

  > h4 {
    margin-top: 0;
  }
`;

const statusBarStyle = (theme: Theme) => css`
  position: absolute;
  left: 0;
  margin-top: -4px;
  width: 4px;
  height: calc(100% + 4px);
  border-radius: 4px;
  background-color: ${theme.color.primary[100]};
`;

const statusBarTypeStyle = css`
  margin-top: -6px;
`;

const typeStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  list-style: none;
  user-select: none;
  color: ${transparentize(0.1, theme.color.foreground)};

  transform: translateY(-2px);

  > svg {
    margin-top: -1px;
    margin-right: 4px;
    color: ${transparentize(0.3, theme.color.foreground)};
  }
`;

const subtitleStyle = (theme: Theme) => css`
  color: ${theme.color.grey[100]};
`;

const subtitleStatusBarStyle = css`
  margin-left: 14px;
`;

const actionsStyle = (theme: Theme) => css`
  display: flex;
  align-self: flex-end;
  gap: 8px;

  ${theme.mediaQuery.medium(css`
    align-self: center;
  `)}
`;

const navigationStyle = css`
  ${layoutStyles}
  margin-top: 1.5rem;
  margin-bottom: calc(-2rem);
  z-index: 10;

  .MuiTab-root {
    z-index: 1;
  }
`;

const breadcrumbsStyle = (theme: Theme) => css`
  ${layoutStyles}
  display: flex;
  margin-top: 8px;
  padding: 0 16px;
  // border-bottom: 2px solid ${theme.color.grey[60]};

  ${theme.mediaQuery.large(css`
    padding: 0;
  `)}

  > ol {
    padding: 4px 10px;
    border-radius: ${borderRadius}px;
    background-color: ${theme.color.grey[60]};
  }
`;
